import React, { useEffect, useRef, useState, useCallback } from 'react';
import './css/styles.css';
import { useHomedata } from '../Admin/Homedata/HomedataProvider';
import { collection, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import { db } from '../Admin/Config/Config';
import { useNavigate } from 'react-router-dom';

const CATEGORY_FETCH_LIMIT = 20;
const SCROLL_BEHAVIOR = 'smooth';

export default function Slider() {
  // Hooks
  const homeData = useHomedata();
  const navigate = useNavigate();
  const sliderRef = useRef(null);
  
  // State
  const [categoryList, setCategoryList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Memoized values
  // const shouldRenderSlider = useMemo(() => 
  //   homeData?.is_row1 && categoryList.length > 0, 
  //   [homeData?.is_row1, categoryList.length]
  // );

  // Fetch categories with error handling
  const fetchCategories = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const categoryQuery = query(
        collection(db, "category"),
        where("status", "==", true),
        orderBy("order", "asc"),
        limit(CATEGORY_FETCH_LIMIT)
      );
      
      const querySnapshot = await getDocs(categoryQuery);
      const categories = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setCategoryList([
        { id: 'all-products', category_name: 'All Products', isAll: true },
        ...categories
      ]);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError('Failed to load categories');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Check scroll position
  const checkScrollPosition = useCallback(() => {
    if (sliderRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = sliderRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  }, []);

  // Effects
  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    const slider = sliderRef.current;
    if (slider) {
      checkScrollPosition();
      slider.addEventListener('scroll', checkScrollPosition);
      window.addEventListener('resize', checkScrollPosition);
      
      return () => {
        slider.removeEventListener('scroll', checkScrollPosition);
        window.removeEventListener('resize', checkScrollPosition);
      };
    }
  }, [checkScrollPosition, categoryList]);

  // Event handlers
  const handleScrollLeft = useCallback(() => {
    if (sliderRef.current) {
      const { offsetWidth } = sliderRef.current;
      sliderRef.current.scrollBy({ 
        left: -offsetWidth, 
        behavior: SCROLL_BEHAVIOR 
      });
    }
  }, []);

  const handleScrollRight = useCallback(() => {
    if (sliderRef.current) {
      const { offsetWidth } = sliderRef.current;
      sliderRef.current.scrollBy({ 
        left: offsetWidth, 
        behavior: SCROLL_BEHAVIOR 
      });
    }
  }, []);

  const handleCategoryNavigation = useCallback((category) => {
    const encodedCategory = encodeURIComponent(category);
    navigate(`/search-items?query=${encodedCategory}&f=${category}`);
  }, [navigate]);

  const handleAllProductsNavigation = useCallback(() => {
    navigate('/search-items');
  }, [navigate]);

  const handleImageLoad = useCallback((event) => {
    event.target.parentNode?.classList.remove('shimmer');
  }, []);

  // Navigate to full categories page
  const handleViewAll = useCallback(() => {
    navigate('/Categories');
  }, [navigate]);

  // Render helpers
  const renderCategoryItem = useCallback((category, index) => {
    const onActivate = category.isAll
      ? handleAllProductsNavigation
      : () => handleCategoryNavigation(category.category_name);

    return (
      <div
        key={`${category.id}-${index}`}
        onClick={onActivate}
        className="flex-shrink-0 w-24 md:w-32 lg:w-32 flex flex-col items-start mr-4 group cursor-pointer"
        role="button"
        tabIndex={0}
        aria-label={`Navigate to ${category.category_name} category`}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onActivate();
          }
        }}
      >
        <div className="p-2 border  border-gray-200 rounded-full flex items-center justify-center aspect-square w-full">
          {category.isAll ? (
            <div className="rounded-full w-full h-full overflow-hidden bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center text-white">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3/4 h-3/4">
                <path d="M3 4.75A1.75 1.75 0 014.75 3h2.5A1.75 1.75 0 019 4.75v2.5A1.75 1.75 0 017.25 9h-2.5A1.75 1.75 0 013 7.25v-2.5zM11 4.75A1.75 1.75 0 0112.75 3h2.5A1.75 1.75 0 0117 4.75v2.5A1.75 1.75 0 0115.25 9h-2.5A1.75 1.75 0 0111 7.25v-2.5zM3 12.75A1.75 1.75 0 014.75 11h2.5A1.75 1.75 0 019 12.75v2.5A1.75 1.75 0 017.25 17h-2.5A1.75 1.75 0 013 15.25v-2.5zM11 12.75A1.75 1.75 0 0112.75 11h2.5A1.75 1.75 0 0117 12.75v2.5A1.75 1.75 0 0115.25 17h-2.5A1.75 1.75 0 0111 15.25v-2.5z" />
              </svg>
            </div>
          ) : (
            <div className="shimmer rounded-full w-full h-full overflow-hidden bg-white">
              <img
                src={category.image}
                alt={category.category_name}
                className="w-full h-full rounded-full object-cover"
                onLoad={handleImageLoad}
                loading="lazy"
              />
            </div>
          )}
        </div>
        
        <span className="text-xs text-left ml-2 text-gray-800 mt-2 font-medium group-hover:text-gray-900 ">
          {category.category_name}
        </span>
      </div>
    );
  }, [handleAllProductsNavigation, handleCategoryNavigation, handleImageLoad]);

  const renderNavigationButtons = () => (
    <div className="flex items-center gap-2">
      <button
        onClick={handleScrollLeft}
        disabled={!canScrollLeft}
        className={`p-2 rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 ${
          canScrollLeft 
            ? 'bg-white text-gray-700 hover:bg-gray-50 hover:text-gray-900 hover:shadow-lg border border-gray-200 hover:border-gray-300' 
            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
        }`}
        aria-label="Scroll categories left"
        type="button"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          fill="currentColor"
          className="bi bi-chevron-left"
          viewBox="0 0 16 16"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z"
          />
        </svg>
      </button>
      
      <button
        onClick={handleScrollRight}
        disabled={!canScrollRight}
        className={`p-2 rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50 ${
          canScrollRight 
            ? 'bg-red-100 text-red-600 border border-red-200 shadow-sm' 
            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
        }`}
        aria-label="Scroll categories right"
        type="button"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          fill="currentColor"
          className="bi bi-chevron-right"
          viewBox="0 0 16 16"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z"
          />
        </svg>
      </button>
    </div>
  );

  const renderLoadingState = () => (
    <div className="flex space-x-4 animate-pulse">
      {Array.from({ length: 6 }).map((_, index) => (
        <div key={index} className="flex-shrink-0 w-24 md:w-32 lg:w-32">
          <div className="p-3 border bg-gray-200 rounded-xl w-full h-26 mb-2"></div>
          <div className="h-3 bg-gray-200 rounded ml-2"></div>
        </div>
      ))}
    </div>
  );

  const renderErrorState = () => (
    <div className="text-center py-8">
      <p className="text-gray-500 mb-4">{error}</p>
      <button
        onClick={fetchCategories}
        className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors duration-200"
      >
        Retry
      </button>
    </div>
  );

  // Early return if slider shouldn't render
  if (!homeData?.is_row1) {
    return null;
  }

  return (
    <section className=" px-6 relative mx-auto max-w-8xl sm:px-12  text-center mt-2 py-4">
      <div className="flex items-center justify-between mb-6">
        <header className="text-left">
          <div className="mb-2">
            <h2 className="text-2xl font-medium bg-gradient-to-r from-gray-800 via-red-500 to-red-600 bg-clip-text text-transparent">
              {homeData.row1}
            </h2>
          </div>
          <div className="w-12 h-1 bg-gradient-to-r from-red-500 to-red-600 rounded-full"></div>
        </header>
        <div className="flex items-center gap-3">
                  {categoryList.length > 4 && renderNavigationButtons()}
          <button
            onClick={handleViewAll}
            type="button"
            className="inline-flex items-center px-4 py-2 rounded-full bg-red-600 text-white text-sm font-medium shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
            aria-label="View all categories"
          >
            View All
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 ml-2" aria-hidden="true">
              <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414-1.414L13.586 10H4a1 1 0 110-2h9.586l-3.293-3.293a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>

        </div>
      </div>

      {error ? (
        renderErrorState()
      ) : isLoading ? (
        renderLoadingState()
      ) : (
        <div className="relative">
          <div 
            className="overflow-x-auto hide-scrollbar scroll-smooth" 
            ref={sliderRef}
            role="region"
            aria-label="Category slider"
          >
            <div className="flex lg:flex-nowrap pb-2">
              {categoryList.map(renderCategoryItem)}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}